/**
 * The system's date display formats — **date-fns** masks, one source of truth.
 *
 * Use these wherever a date is **displayed**: filter triggers, `ReadOnly` fields, table cells,
 * chart axes, anywhere a screen prints a date. A date should read the same everywhere, and until
 * 2026-08-27 it did not — filters printed "May 5, 2026" while the picker and ReadOnly docs
 * suggested "5 May 2026". Unified on the filter form (designer's call).
 *
 * NOT design tokens: these are a locale/copy convention, not Figma Variables, so they live here
 * rather than in `src/theme/tokens`.
 *
 * These are for **reading**, not for **typing**. The editable picker fields keep the adapter's
 * locale default numeric mask, because a month name is slower to type and arrow through than
 * digits — a separate decision from how a date is presented (see docs/OPEN_ITEMS.md).
 *
 * ⚠️ These are date-fns tokens (lowercase `d`, lowercase `yyyy`), NOT dayjs ones. The two
 * libraries spell the same mask differently — dayjs' `'MMM D, YYYY'` is date-fns' `'MMM d, yyyy'`
 * — and date-fns *throws* on `D`/`YYYY` rather than failing quietly, so a stale mask surfaces
 * immediately. See docs/DECISIONS.md (2026-09-16, dayjs → date-fns).
 *
 * @example
 * import { format } from 'date-fns';
 * import { dateFormats } from '@openx/design-system';
 *
 * <ReadOnly label="Start date" value={format(date, dateFormats.full)} />   // May 5, 2026
 */

import type { ConstValues } from './typeHelpers.ts';
export const dateFormats = {
  /** A date carrying its own year — the default for anything standalone. "May 5, 2026" */
  full: 'MMM d, yyyy',
  /**
   * Day and month only, for a date whose year is already stated nearby — e.g. the start of a
   * same-year range, where the year lands once at the end. "May 5"
   */
  dayMonth: 'MMM d',
} as const;

export type DateFormat = ConstValues<typeof dateFormats>;
